import React, {FC, useEffect} from 'react';
import {connect} from 'react-redux';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
  wrapInShallowSelector,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {ResolveDispatchProps, useAppDispatch} from '../../../helpers/reduxHelpers';
import {AppState} from '../../../store/appState';
import {addCardsToAccountAction} from '../../../store/data/accounts/accountsActions';
import {fetchCrmSyncsAsyncAction} from '../../../store/data/crmSync/crmSyncsActions';
import {getAccounts} from '../../../store/data/dataSelectors';
import {updateAccountDetailsSectionExpandedAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {getAccountDetailsSectionsExpandState} from '../../../store/data/localPreferences/localPreferencesSelectors';
import {
  getHasCrmSyncFullPageSalesforce,
  getHasHubspotContactSync,
} from '../../../store/featureFlags/featureFlagsSelectors';
import {
  fetchAccountForAccountDetailsAction,
  fetchCardsForAccountDetailsAction,
  navigateAccountDetailsAction,
} from '../../../store/panels/accounts/accountDetailsPanelActions';
import {makeGetAccountDetailsPanel} from '../../../store/panels/accounts/accountDetailsPanelSelectors';
import {AccountDetailsCardsState} from '../../../store/panels/accounts/accountDetailsPanelState';
import {ContactsManagerParametersTypesEnum} from '../../../store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {AccountDetailsPanelId, ContactOrAccountDetailsPanelId} from '../../../store/panels/panelStateBase';
import {inLegacyTransientPanel} from '../../legacyTransientPanel';
import {AccountDetailsTrackEventHandler} from '../tracking/accountDetailsTrackingHelpers';
import {AccountDetails} from './accountDetails';

/*
 * Types.
 */

interface AccountDetailsContainerPublicProps {
  panelId: ContactOrAccountDetailsPanelId;
  accountId: number;
  status?: AsyncStatusesEnum;
  cardsState?: AccountDetailsCardsState;
  isEmbedded?: boolean;
  conversationStatus?: AsyncStatusesEnum;
  conversationIds?: ReadonlyArray<number>;
  hasMoreConversations?: boolean;
  location?: ContactsManagerParametersTypesEnum;
  onTrackEvent?: AccountDetailsTrackEventHandler;
}

type AccountDetailsContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type AccountDetailsContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type AccountDetailsContainerProps = AccountDetailsContainerPublicProps &
  AccountDetailsContainerStateProps &
  AccountDetailsContainerDispatchProps;

/*
 * Selectors.
 */

function getPanelId(_state: AppState, props: AccountDetailsContainerPublicProps) {
  return props.panelId;
}

function getAccountId(_state: AppState, props: AccountDetailsContainerPublicProps) {
  return props.accountId;
}

export function makeGetAccount() {
  return createSelector([getAccountId, getAccounts], (accountId, accounts) =>
    accountId ? accounts[accountId] : undefined,
  );
}

/*
 * Component.
 */

const AccountDetailsDetailsContainerComponent: FC<AccountDetailsContainerProps> = (props) => {
  const {
    panelId,
    accountId,
    account,
    sectionExpandState,
    accountDetailsPanel,
    status,
    cardsState,
    isEmbedded,
    conversationStatus,
    conversationIds,
    hasMoreConversations,
    location,
    hasHubspotContactSync,
    hasCrmSyncFullPageSalesforce,
    fetchAccount,
    onAccountDetailsSectionToggled,
    addCardsToAccount,
    fetchCardsForAccountDetails,
    onTrackEvent,
  } = props;

  // If the account has external info attached to it, make sure we have the latest crm sync information.
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (account?.externalInfo) {
      dispatch(fetchCrmSyncsAsyncAction());
    }
  }, [dispatch, account?.externalInfo]);

  useEffect(() => {
    // Do not fetch data here when we are embedded or already have the account
    if (isEmbedded || account) {
      return;
    }
    fetchAccount(panelId, accountId);
  }, [panelId, account, accountId, isEmbedded, fetchAccount]);

  // If we do not have the panel state, something went wrong.
  if (!accountDetailsPanel && !isEmbedded) {
    return null;
  }

  const addCardToAccount = async (cardId: number) => {
    if (!account) {
      return;
    }
    await addCardsToAccount(account.id, [cardId]);
    fetchCardsForAccountDetails(panelId, account.id);
  };

  return (
    <AccountDetails
      panelId={panelId}
      accountId={accountId}
      account={account}
      status={status ?? accountDetailsPanel?.status ?? AsyncStatusesEnum.FAILED}
      cardsState={cardsState ?? accountDetailsPanel?.cardsState}
      popupAppPanelId={accountDetailsPanel?.popupAppPanelId}
      conversationStatus={conversationStatus ?? accountDetailsPanel?.conversationStatus}
      conversationIds={conversationIds ?? accountDetailsPanel?.conversationIds}
      hasMoreConversations={hasMoreConversations ?? accountDetailsPanel?.hasMoreConversations}
      sectionExpandState={sectionExpandState}
      isEmbedded={isEmbedded}
      onAccountDetailsSectionToggled={onAccountDetailsSectionToggled}
      addCardToAccount={addCardToAccount}
      fetchAccount={fetchAccount}
      onTrackEvent={onTrackEvent}
      location={location}
      hasHubspotContactSync={hasHubspotContactSync}
      hasCrmSyncFullPageSalesforce={hasCrmSyncFullPageSalesforce}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AccountDetailsContainerPublicProps>()(() => ({
  accountDetailsPanel: makeGetAccountDetailsPanel(getPanelId),
  account: makeGetAccount(),
  hasHubspotContactSync: getHasHubspotContactSync,
  hasCrmSyncFullPageSalesforce: getHasCrmSyncFullPageSalesforce,
  sectionExpandState: wrapInShallowSelector(getAccountDetailsSectionsExpandState),
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  fetchAccount: fetchAccountForAccountDetailsAction,
  onAccountDetailsSectionToggled: updateAccountDetailsSectionExpandedAction,
  addCardsToAccount: addCardsToAccountAction,
  fetchCardsForAccountDetails: fetchCardsForAccountDetailsAction,
}));

export const AccountDetailsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AccountDetailsDetailsContainerComponent);

export const TransientAccountDetailsContainer = inLegacyTransientPanel(
  AccountDetailsContainer,
  // We can type cast panelId as this will only be called from the account details panel
  ({panelId, accountId}) => navigateAccountDetailsAction(panelId as AccountDetailsPanelId, accountId),
);
